import { InlineNotification, Table, TableBody, Tile } from '@carbon/react';
import { warningColors } from './constants/warningColors';
import styles from './allocation-manager.module.scss';
import { useMemo } from 'react';
import { AllocationManagerTableHeaders } from './components/allocation-manager-table-headers';
import { AllocationManagerLineItems } from './components/allocation-manager-line-items';
import { AllocationManagerTotalRows } from './components/allocation-manager-total-rows';
import { useAllocationWarnings } from './hooks/use-allocation-warnings';
import { AllocationManagerResponse } from 'types/api-responses';

export interface AllocationManagerProps {
  allocationManagerData: AllocationManagerResponse;
  onLineItemClick: (tractId: number | null) => void;
  selectedTractId?: number | null;
  selectedVersion?: string;
}

export const AllocationManager = ({
  allocationManagerData,
  onLineItemClick,
  selectedTractId,
  selectedVersion,
}: AllocationManagerProps) => {
  const activeHeader = allocationManagerData.allocationMethod;

  const renderLateralLengthValues = useMemo(() => {
    if (
      allocationManagerData.allocationMethod?.toLowerCase() === 'acreage' &&
      allocationManagerData.lineItems.every(
        (tract) =>
          !(
            tract.unitAllocationTractLateralValue ||
            tract.unitAllocationTractLateralFactor
          )
      )
    ) {
      return false;
    }
    return true;
  }, [allocationManagerData.allocationMethod, allocationManagerData.lineItems]);

  const totals = {
    acreageValue: allocationManagerData.totalAcreageSum,
    acreageFactor: allocationManagerData.totalAcreageFactor,
    acreageGisValue: allocationManagerData.totalGisAcreageSum,
    lateralValue: allocationManagerData.totalLateralSum,
    lateralFactor: allocationManagerData.totalLateralFactor,
    lateralGisValue: allocationManagerData.totalGisLateralSum,
  };
  const checkAllocations = useAllocationWarnings(
    allocationManagerData.lineItems,
    totals,
    renderLateralLengthValues,
    allocationManagerData.unitPlatGrossAcres,
    allocationManagerData.wellPlatTotalLateralLength,
    allocationManagerData.totalGisAcreageSourced,
    allocationManagerData.totalGisLateralSourced
  );

  const renderContent = () => {
    // Handle no allocation manager line items
    if (
      !allocationManagerData ||
      !allocationManagerData.lineItems ||
      !allocationManagerData.lineItems.length
    ) {
      // Check if unit shape changed (version pattern X.1 where X > 1)
      if (
        selectedVersion &&
        /^\d+\.1$/.test(selectedVersion) &&
        parseInt(selectedVersion) > 1
      ) {
        return (
          <InlineNotification
            kind="info"
            title="The unit shape has changed and invalidated the allocation manager."
            lowContrast
            subtitle="Please contact a GIS team for assistance"
            hideCloseButton
          />
        );
      }

      // Default: no data available
      return (
        <InlineNotification
          kind="info"
          title="No data is available at this time."
          lowContrast
          subtitle="Please contact a GIS team for assistance"
          hideCloseButton
        />
      );
    }
    // Render the actual content
    return (
      <>
        {checkAllocations.map((warning) => (
          <InlineNotification
            key={warning.id}
            kind="warning"
            title={warning.title}
            subtitle={warning.subtitle}
            lowContrast
            className={styles.warning}
            hideCloseButton
            style={{
              backgroundColor:
                warningColors[warning.id as keyof typeof warningColors],
            }}
          />
        ))}
        <Tile className={styles.tableContainer}>
          <Table>
            <AllocationManagerTableHeaders
              renderLateralLengthValues={renderLateralLengthValues}
              uwi={allocationManagerData.well.uwi}
              activeHeader={activeHeader}
            />
            <TableBody>
              <AllocationManagerLineItems
                items={allocationManagerData.lineItems}
                renderLateralLengthValues={renderLateralLengthValues}
                onLineItemClick={onLineItemClick}
                selectedTractId={selectedTractId}
              />
              {totals && (
                <AllocationManagerTotalRows
                  checkAllocations={checkAllocations}
                  totals={totals}
                  renderLateralLengthValues={renderLateralLengthValues}
                  unitPlatGrossAcres={allocationManagerData.unitPlatGrossAcres}
                  wellPlatTotalLateralLength={
                    allocationManagerData.wellPlatTotalLateralLength
                  }
                  totalGisAcreageSourced={
                    allocationManagerData.totalGisAcreageSourced
                  }
                  totalGisLateralSourced={
                    allocationManagerData.totalGisLateralSourced
                  }
                />
              )}
            </TableBody>
          </Table>
        </Tile>
      </>
    );
  };

  return renderContent();
};
